import { useCallback } from "react";

import { useSelector } from "react-redux";
import { isEqual } from "lodash-es";

import { featureEnabled } from "../../../../Feature";
import { useLibrary } from "../../../providers";
import { ProductType } from "../../../../Account";

import {
  selectAllAccounts,
  selectBAccount,
  selectBAccounts,
  selectOneAccount,
} from "./selectors";

import type { AccountProductTypeToFluentId } from "./types";
import type { RootState } from "../store";
import type Account from "../../../../Account";

export const isTransferAvailableForExternalAccount = (
  account: Pick<Account, "isExternal" | "verified">,
  achEnabled: boolean,
): boolean => {
  if (!account.isExternal()) {
    return true;
  }

  return account.verified && achEnabled;
};
const ACCOUNT_PRODUCT_TYPE_TO_FLUENT_ID = Object.fromEntries(
  Object.values(ProductType).map((pt) => [pt, `account-type-${pt}`]),
) as AccountProductTypeToFluentId;

export const useBAccount = (id: API.AccountId): Account | undefined => {
  return useSelector((state: RootState) => selectBAccount(state, id), isEqual);
};

export const useAccounts = () => {
  return useSelector(selectBAccounts);
};

export const useAllAccounts = () => {
  return useSelector(selectAllAccounts);
};

export const useAccount = (accountId?: API.AccountId): Account | undefined => {
  return useSelector((state) => selectOneAccount(state, accountId), isEqual);
};

const useAchEnabledForExternalTransfers = (): boolean => {
  return useSelector((state: RootState) => {
    const features = {
      ...(state.featureStatus?.ga || {}),
      ...(state.user?.user?.features || state.institution.data?.features || {}),
      ...(state.endToEndTest?.featureOverrides || {}),
    } as API.Features;

    return featureEnabled(features, { or: "ach" });
  });
};

export const useValidTransferSources = (
  selectedDestinationAccountId?: Account["id"],
  supportsInternalTransfers: boolean = true,
): Account[] => {
  const accounts = useAccounts();
  const achEnabled = useAchEnabledForExternalTransfers();
  const selectedDestinationAccount = accounts.find(
    (a) => a.id === selectedDestinationAccountId,
  );
  return accounts.filter((a) => {
    if (!isTransferAvailableForExternalAccount(a, achEnabled)) {
      return false;
    }
    return a.isValidTransferSourceForDestination(
      selectedDestinationAccount,
      supportsInternalTransfers,
    );
  });
};

export function useValidTransferDestinations(
  achAllowsPush: boolean,
  selectedSourceAccountId?: Account["id"],
  supportsInternalTransfers: boolean = true,
  areLoansAllowed: boolean = true,
): Account[] {
  const accounts = useAccounts();
  const achEnabled = useAchEnabledForExternalTransfers();
  const selectedSourceAccount = accounts.find(
    (a) => a.id === selectedSourceAccountId,
  );
  return accounts.filter((a) => {
    if (!isTransferAvailableForExternalAccount(a, achEnabled)) {
      return false;
    }
    return a.isValidTransferDestinationForSource(
      selectedSourceAccount,
      achAllowsPush,
      supportsInternalTransfers,
      areLoansAllowed,
    );
  });
}

export const useAccountProductTypeName = () => {
  const l10n = useLibrary("translations");
  return useCallback(
    (rawAccountType: API.ProductType = "unknown"): string =>
      l10n.getString(ACCOUNT_PRODUCT_TYPE_TO_FLUENT_ID[rawAccountType]),
    [l10n],
  );
};
